import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import {
    ClientErrorCodes,
    ErrorCodes,
    ErrorStatus,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@services/auth.service';
import { ServerConfiguration } from '@services/configuration/server.configuration';
import {
    FOLDER_HEADER_NAME,
    LOCALIZATION_HEADER_NAME,
} from '@services/generics/constants';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { extractErrorMessage } from '@utils/object.utils';
import { isJson } from '@utils/string.utils';

import {
    Observable,
    of,
    throwError,
} from 'rxjs';
import {
    catchError,
    switchMap,
} from 'rxjs/operators';

import { ERROR_MESSAGES } from './error.messages';

export const INTERCEPTOR_DEPENDENCIES = [
    ServerConfiguration,
    TranslateService,
    AuthService,
    UiSnackBarService,
    AppStateFacade,
];

// we need to exclude these URLs to get the information from the database
const LOCALIZATION_EXCEPTION_URLS = [
    '/odata/Settings/UiPath.Server.Configuration.OData.GetAuthenticationSettings',
    '/odata/Settings/UiPath.Server.Configuration.OData.GetWebSettings',
];

@Injectable()
export class Interceptor implements HttpInterceptor {
    constructor(
        private _serverConfig: ServerConfiguration,
        private _translateService: TranslateService,
        private _authService: AuthService,
        private _snack: UiSnackBarService,
        private _appState: AppStateFacade,
    ) { }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const analyticsBaseAddress = this._appState.settings.get(Setting.analyticsBaseAddress);
        // FIXME: analyticsBaseAddress can be undefined | '' if url not set up.
        if (analyticsBaseAddress && request.url.indexOf(analyticsBaseAddress) > -1) {
            return next.handle(request.clone({
                withCredentials: true,
            })).pipe(
                catchError((error) => this._handleError(error, false)),
            );
        }

        // set our CORS option
        const options = {
            url: request.url,
            setHeaders: {} as any,
            withCredentials: true,
        };

        // set the current language header
        if (
            this._appState.language.value !== null &&
            !LOCALIZATION_EXCEPTION_URLS.find(url => request.url.startsWith(url))
        ) {
            options.setHeaders[LOCALIZATION_HEADER_NAME] = this._appState.language.value;
        }

        // set the json content-type unless we:
        //  1. upload files  OR
        //  2. download files
        if (!(request.body instanceof FormData) &&
            request.responseType !== 'blob') {
            options.setHeaders['Content-Type'] = 'application/json';
        }
        // set the folder id header
        if (
            this._appState.user.folder.hasAny &&
            !request.url.includes('api/FoldersNavigation/GetFolderContext') &&
            !request.headers.has(FOLDER_HEADER_NAME)
        ) {
            options.setHeaders[FOLDER_HEADER_NAME] = this._appState.user.folder.value!.id.toString();
        }

        const normalizedUrl = this._serverConfig.baseHref.endsWith('/') && options.url.startsWith('/') ?
            options.url.substring(1) : options.url;

        options.url = this._serverConfig.baseHref + normalizedUrl;

        // when sending request to external sites directly, we don't override with options which is
        // used for internal Orchestrator APIs only.
        request = this._isSafeOverride(this._serverConfig.baseHref, request.url) ? request.clone(options) : request;

        const ignoreInvalidFolder =
            request.headers.has(FOLDER_HEADER_NAME) &&
            request.url.includes('/odata/Users/UiPath.Server.Configuration.OData.GetCurrentPermissions');

        return next.handle(request).pipe(
            catchError((error) => this._handleError(error, ignoreInvalidFolder)),
        );
    }

    private _handleError(errorResponse: HttpErrorResponse | any, ignoreInvalidFolder: boolean): Observable<HttpEvent<any>> {
        if (errorResponse instanceof HttpErrorResponse) {
            let error = errorResponse.error;
            const contentType = errorResponse.headers.get('content-type');
            const isJsonRequest = contentType && contentType.startsWith('application/json');
            if (error && error.length && isJsonRequest) {
                error = JSON.parse(error);
                errorResponse = new HttpErrorResponse({
                    error,
                    headers: errorResponse.headers,
                    status: errorResponse.status,
                    statusText: errorResponse.statusText,
                    url: errorResponse.url!,
                });
            }
        }

        const errorObservable = throwError(errorResponse);

        if (errorResponse.status === 400) {

            if (isJson(errorResponse._body) || errorResponse.error) {
                const errorResult = errorResponse.error || JSON.parse(errorResponse._body) || {};

                // if the folder set to the user is no longer valid
                if (errorResult.errorCode === ErrorCodes.InvalidFolder) {
                    if (ignoreInvalidFolder) {
                        return of({ Permissions: [] }) as any;
                    }

                    this._snack.error(`${errorResult.message} (#${ErrorCodes.InvalidFolder})`);
                    return this._appState.user.ensureValidFolders()
                        .pipe(
                            switchMap(() => errorObservable),
                        );
                }
            }

            if (errorResponse.statusText === ErrorStatus.InvalidAntiForgeryToken) {
                this._snack.error(`${this._translateService.instant('generic_session_expired')} (#{${ClientErrorCodes.SessionExpired}})`);
                this._performLogout();
                errorResponse.wasShown = true;
                return errorObservable;
            }
        }

        const defaultMessage = `${this._translateService.instant('error_generic')} (#${ClientErrorCodes.GenericError})`;
        let message = extractErrorMessage(errorResponse, defaultMessage);

        // handle 401 first
        // then handle 403 for GetCurrentPermissions as all
        // logged in users should be able to call GetCurrentPermissions
        if (errorResponse.status === 401 ||
            (errorResponse.status === 403 && errorResponse.url.indexOf('GetCurrentPermissions') > -1)) {
            this._performLogout();
            if (message) {
                errorResponse.wasShown = true;
                this._snack.error(message);
            }
            return errorObservable;
        }

        if ([504, 0].includes(errorResponse.status)) {
            // fallback to English if translations aren't available
            const fallback = 'Could not connect to the server';
            const translation = this._translateService.instant('generic_connection_error');
            message = translation === 'generic_connection_error' ? fallback : translation;
            message += ` (#${ClientErrorCodes.NetworkError})`;
        } else if (errorResponse.error && ERROR_MESSAGES.has(errorResponse.error.errorCode)) {
            message = this._translateService.instant(ERROR_MESSAGES.get(errorResponse.error.errorCode)!);
        }

        if (message) {
            errorResponse.wasShown = true;
            this._snack.error(message);
        }

        return errorObservable;
    }

    private _performLogout() {
        const redirect = window.location.pathname.replace(this._serverConfig.baseHref, '');

        if (redirect !== '/account/login') {
            const params = redirect ? { queryParams: { returnUrl: redirect } } : {};
            this._authService.logout(params);
        }
    }

    private _isSafeOverride(base: string, url: string): boolean {
        // for urls like http://localhost:6234/mlpackages?ou=1&tid=1
        if (url.startsWith(base)) {
            return true;
        }
        // for urls like /odata/MLPackages?ou=1&tid=1
        const pattern = /^((http|https):\/\/)/;
        if (!pattern.test(url)) {
            return true;
        }
        // for the rest, assume they are for external sites
        return false;
    }
}
